// ChatBlob.js
import { authHeader } from '../../utils/authHeaders';
// Update import to include useEffect
import React, { useState, useEffect } from 'react'
import ContentRenderer, { DocumentSourcePill, isInlineCode } from '../ContentRenderer'
import constants, { BASE_QUERYURL, BASE_URL, BASE_FILEURL, BASE_WORDURL, getInitials } from '../constants'
import { useDispatch, useSelector } from 'react-redux'
import { addSource, fetchGeneratedPdf } from '../../slice/sourceSlice'
import { fetchGeneratedDocuments } from '../../slice/apiSlice'
import { fetchPDF } from '../../slice/pdfViewslice'
import { selectAIProfile } from '../../slice/aiProfileSlice'
import CommentSection from './CommentSection';
import AnswerComments from './AnswerComments';
import { processMessageSources, isSourceClickable } from '../../utils/sourceValidator';
import SourceDebugger from './SourceDebugger';
import { selectTaskStatusBySqId } from '../../slice/taskStatusSlice';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';
import { ChevronDown, ChevronUp, Globe, RotateCcw, SquareTerminal } from 'lucide-react';
import WordIcon from '../icons/WordIcon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeAiMarkdownText, remarkNestLabeledListItems, isLabelOnlyLiNode } from '../../utils/markdownNormalize';
import { downloadAuthenticatedFile } from '../../utils/downloadFile';
import { toast } from 'react-hot-toast';
import { AnalysisJobCard, AnalysisResultPanel } from './AnalysisJobCard';
import { startDataAnalysis, retryAnalysisJob } from '../../slice/chatSlice';

// Resolves a citation's folder path from the same file list ChatInput's "/"
// picker reads — item_id match first (exact, survives duplicate filenames),
// falls back to filename when a citation predates item_id tracking. Returns
// null (not shown at all) rather than a wrong guess when nothing matches.
function resolveFolderPath(files, filename, itemId, restrictAreaLabel) {
  if (!files || !files.length) return null;
  const match = (itemId && files.find((f) => f.sharepoint_item_id === itemId))
    || files.find((f) => f.filename === filename);
  if (!match) return null;
  // Site + real SharePoint folder path — deliberately NOT every Knowledge
  // Area the file happens to be tagged with (it's a categorization label
  // the same file carries everywhere, not part of where it actually
  // lives; showing all of them made an ordinary citation look like it
  // came from several different places at once). When this message's own
  // question was restricted to one file via ChatInput's "/" picker,
  // restrictAreaLabel carries the ONE area group it was actually picked
  // from (echoed via chatSlice.js) — that's the only case where a single
  // area is unambiguous, so it's prepended here to match.
  const sitePart = match.sharepoint_site_name || '';
  const folderPath = match.sharepoint_file_path
    ? match.sharepoint_file_path.split('/').filter(Boolean).slice(0, -1).join('/')
    : (match.folder_name || '');
  const siteAndFolder = [sitePart, folderPath].filter(Boolean).join('/');
  if (!siteAndFolder) return null;
  const areaName = restrictAreaLabel ? restrictAreaLabel.split(' — ')[0] : null;
  return areaName ? `${areaName} — ${siteAndFolder}` : siteAndFolder;
}

function sanitizeTitleForId(title) {
    return title
      ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      : '';
}

function formatFileSize(bytes) {
    if (!bytes || bytes <= 0) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

// The finished-document card — same shape whether it's rendered live (right
// after WordJobStatus's poll resolves) or after a page reload
// (message.generated_document_id, no polling needed). Preview reuses the
// exact sidebar/PDF-viewer pipeline document citations already use
// (state.source.currentPdf) — see sourceSlice.js's fetchGeneratedPdf —
// rather than a separate viewer, so it needs nothing new from Sidebar.jsx.
function WordResultCard({ title, filename, fileSize, documentId, userId, t, aispaceColor, openSidebar }) {
    const dispatch = useDispatch();
    const handlePreview = () => {
        openSidebar();
        dispatch(fetchGeneratedPdf({ documentId, userId, filename: filename || title }));
    };
    const handleDownload = (format) => {
        const name = format === 'pdf' ? (filename || title || 'document').replace(/\.docx$/, '.pdf') : (filename || title || 'document');
        downloadAuthenticatedFile(`${BASE_WORDURL}/download/${documentId}?format=${format}`, name)
            .catch(() => toast.error(t('chatActions.wordDownloadError')));
    };
    const sizeLabel = formatFileSize(fileSize);
    return (
        <div>
            <p className="text-[14px] font-semibold text-[#111827] mb-4">{t('chatActions.wordDocumentCreated')}</p>
            <div className="border border-[#E5E7EB] rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                        <WordIcon size={32} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-[#111827] truncate">{filename || title}</p>
                        {sizeLabel && <p className="text-[12px] text-[#9CA3AF]">{sizeLabel}</p>}
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ECFDF5] text-[12px] font-medium text-[#059669] flex-shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                        {t('chatActions.wordReady')}
                    </span>
                </div>
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] text-[#374151]">{t('chatActions.wordCompletedLabel')}</span>
                        <span className="text-[12px] text-[#9CA3AF]">100%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: '100%', backgroundColor: aispaceColor }} />
                    </div>
                </div>
                <p className="mt-3 text-[12px] text-[#9CA3AF]">{t('chatActions.wordReadyHelper')}</p>
                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handlePreview}
                        className="px-3 py-1.5 rounded-lg border border-[#E5E7EB] text-[13px] font-medium text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                    >
                        {t('chatActions.wordPreview')}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleDownload('docx')}
                        className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-white transition-colors"
                        style={{ backgroundColor: aispaceColor }}
                    >
                        ↓ {t('chatActions.wordDownloadWordShort')}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleDownload('pdf')}
                        className="px-3 py-1.5 rounded-lg border border-[#E5E7EB] text-[13px] font-medium text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                    >
                        ↓ {t('chatActions.wordDownloadPdfShort')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Word-generation job status, self-contained (same "poll and self-hide"
// shape as Analysis.jsx's WebSearchLoadingIndicator, adapted cadence since
// generation takes minutes not seconds — no need for 400ms). Only used for
// a LIVE, still-in-flight job (message.wordJob.job_id) — once a page
// reloads, message.generated_document_id is already resolved server-side,
// so that case renders WordResultCard directly instead (see ChatBlob body),
// no polling needed.
function WordJobStatus({ jobId, userId, historyId, t, aispaceColor, openSidebar }) {
    const dispatch = useDispatch();
    const [status, setStatus] = useState('running');
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!jobId) return;
        let cancelled = false;
        let timer;
        const poll = async () => {
            try {
                const res = await fetch(`${BASE_WORDURL}/status/${jobId}`, { headers: { ...authHeader() } });
                const data = await res.json();
                if (cancelled) return;
                setProgress(data.progress || 0);
                if (data.status === 'running') {
                    timer = setTimeout(poll, 3000);
                } else {
                    setStatus(data.status); // done | nothing_found | truncated | error
                    if (data.status === 'done' && data.result) {
                        setResult(data.result);
                        // Sidebar's "Generated files" tab lives outside this
                        // component tree — refetch it now so the new file
                        // shows up (and the count bumps) the moment this job
                        // finishes, without waiting for a page reload.
                        dispatch(fetchGeneratedDocuments({ userId, historyId }));
                    }
                }
            } catch (e) {
                // Transient network hiccup — keep polling rather than flipping
                // to a false "error" over one failed request.
                if (!cancelled) timer = setTimeout(poll, 3000);
            }
        };
        timer = setTimeout(poll, 1500);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [jobId]);

    if (status === 'done' && result) {
        return (
            <WordResultCard
                title={result.title}
                filename={result.filename}
                fileSize={result.file_size}
                documentId={result.document_id}
                userId={userId}
                t={t}
                aispaceColor={aispaceColor}
                openSidebar={openSidebar}
            />
        );
    }

    if (status === 'running') {
        return (
            <div className="border border-[#E5E7EB] rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                        <WordIcon size={32} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-[#111827]">{t('chatActions.wordCreating')}</p>
                        <p className="text-[13px] text-[#6B7280]">{t('chatActions.wordCreatingSubtitle')}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F3F4F6] text-[12px] font-medium text-[#374151] flex-shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: aispaceColor }} />
                        {t('chatActions.wordCreatingStatusPill')}
                    </span>
                </div>
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] text-[#374151]">{t('chatActions.wordCreatingRendering')}</span>
                        <span className="text-[12px] text-[#9CA3AF]">{t('chatActions.wordCreatingInProgress')}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
                        <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(4, progress)}%`, backgroundColor: aispaceColor }}
                        />
                    </div>
                </div>
                <p className="mt-3 text-[12px] text-[#9CA3AF]">{t('chatActions.wordCreatingHelper')}</p>
            </div>
        );
    }
    if (status === 'nothing_found') {
        return <p className="text-[14px] text-[#6B7280]">{t('chatActions.wordNothingFound')}</p>;
    }
    if (status === 'truncated') {
        return <p className="text-[14px] text-[#DC2626]">{t('chatActions.wordTruncated')}</p>;
    }
    if (status === 'error') {
        return <p className="text-[14px] text-[#DC2626]">{t('chatActions.wordFailed')}</p>;
    }

    // status === 'done' but the poll response somehow carried no result —
    // shouldn't happen (backend always sets one alongside 'done'), kept only
    // as a non-crashing fallback.
    return null;
}

function getDomain(url) {
    try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
}

function getSiteName(url) {
    const domain = getDomain(url);
    const mainPart = domain.split('.')[0] || domain;
    return mainPart.charAt(0).toUpperCase() + mainPart.slice(1);
}

const AVATAR_COLORS = ['#DC2626', '#2563EB', '#059669', '#D97706', '#7C3AED', '#DB2777', '#0891B2', '#65A30D'];

// Deterministic so the same site always gets the same fallback color,
// instead of a random one flickering between re-renders.
function getAvatarColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    }
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Google's favicon service, with a colored-letter fallback for sites it
// can't resolve — same graceful-degradation pattern as a user avatar.
function SiteIcon({ url, siteName, size = 16 }) {
    const domain = getDomain(url);
    const [failed, setFailed] = useState(false);
    const label = siteName || domain || '?';
    const roundedClass = size >= 32 ? 'rounded-lg' : size >= 24 ? 'rounded-md' : 'rounded';
    if (failed || !domain) {
        return (
            <span
                className={`inline-flex flex-shrink-0 items-center justify-center ${roundedClass} text-white font-semibold leading-none`}
                style={{ width: size, height: size, fontSize: Math.max(8, size * 0.6), backgroundColor: getAvatarColor(label) }}
            >
                {label.charAt(0).toUpperCase()}
            </span>
        );
    }
    return (
        <img
            src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
            alt=""
            width={size}
            height={size}
            className={`flex-shrink-0 ${roundedClass}`}
            style={{ width: size, height: size }}
            onError={() => setFailed(true)}
        />
    );
}

// Safety net for two things question.py's Hybrid branch is meant to strip
// from the model's raw answer before saving it: (1) a redundant old-style
// "(Source: file.pdf, Page N)" / "(Quelle: ..., Seite N)" plain-text citation
// the model sometimes writes right next to the new "[file.pdf, Page N](#)"
// markdown-link citation for the same fact (duplicating it visually), and
// (2) a trailing "Sources:"/"Quellen:" list block at the end of the answer,
// duplicating the app's own separate Sources panel. The backend's own strip
// only runs on newly-generated answers, so this covers messages already
// persisted to the DB from before that fix too — same reasoning as
// ContentRenderer.js's stripTrailingSourcesSection for Documents mode.
const stripDuplicateHybridCitations = (text) => {
    if (!text) return text;
    return text
        .replace(
            /\*?\(\s*(?:Source|Quelle):?\s*.+?\.(?:pdf|docx?|xlsx?|pptx?|txt|csv|png|jpe?g),\s*(?:Page|Seite)\s*\d+\s*\)\*?\s*/gi,
            ''
        )
        .split(/\n+#{0,3}\s*\*{0,2}(?:Sources|Quellen):?\*{0,2}:?\s*/i)[0]
        .trimEnd();
};

// The model sometimes wraps a plain "Label: https://..." reference list in a
// markdown code fence (presumably for visual alignment) instead of a normal
// list — inside a REAL code fence, markdown link syntax never parses (that's
// what a code block is), so the whole thing renders as inert monospace text
// with no clickable links at all. Rather than trying to guess "is this
// actually code or not" (fragile, and would risk mangling a genuine code
// snippet the model legitimately shared), this only turns bare URLs already
// present in the block into real clickable links — a genuine code snippet
// has no bare "https://..." text to match, so it renders exactly as before.
const linkifyCodeBlockUrls = (text) => {
    if (typeof text !== 'string') return text;
    const urlPattern = /https?:\/\/[^\s<>\])"']+/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = urlPattern.exec(text)) !== null) {
        let url = match[0];
        // Trailing sentence punctuation ("...see https://x.com.") isn't part
        // of the URL — leave it as plain text after the link.
        const trailingPunct = url.match(/[.,;:!?]+$/);
        if (trailingPunct) {
            url = url.slice(0, -trailingPunct[0].length);
        }
        const start = match.index;
        if (start > lastIndex) parts.push(text.slice(lastIndex, start));
        parts.push(
            <a key={`code-url-${key++}`} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                {url}
            </a>
        );
        lastIndex = start + url.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 1 ? parts : text;
};

function WebSearchContent({ content, sources, docSources, onSourceClick }) {
    if (!content) return null;

    // Replace [N] with a markdown link using a special __cite_N__ href that won't be sanitized away.
    // NOTE: the placeholder must not contain a colon — remark/mdast treats "scheme:" (e.g. the
    // "cite:" in "__cite:0__") as a URI scheme and silently drops the whole destination, which
    // resolves to href="" and made every citation open the current page instead of the source.
    // The model also often writes multiple citations back-to-back with no separator (e.g.
    // "[1][2][3]"), which renders as visually merged badges ("123") — insert a thin space
    // between them so each citation stays a distinct, clickable number.
    const processed = normalizeAiMarkdownText(stripDuplicateHybridCitations(content))
        .replace(/\[(\d+)\]/g, (match, num) => {
            const idx = parseInt(num, 10) - 1;
            const src = sources && sources[idx];
            return src && src.url ? `[${num}](__cite_${idx}__)` : match;
        })
        .replace(/__\)(\[\d+\]\(__cite_)/g, '__) $1');

    // Every source URL that actually got turned into a "[N]" -> __cite_N__
    // pill above — used below to catch the model ALSO writing that same URL
    // out again as an ordinary link elsewhere in the answer (e.g. literally
    // answering "give me the link" with the raw URL), which duplicates the
    // pill rather than adding new information.
    const citedUrls = new Set();
    (processed.match(/__cite_\d+__/g) || []).forEach((m) => {
        const idx = parseInt(m.replace('__cite_', '').replace('__', ''), 10);
        const url = sources && sources[idx] && sources[idx].url;
        if (url) citedUrls.add(url.replace(/\/$/, ''));
    });

    const citationLink = ({ href, children }) => {
        if (href && href.startsWith('__cite_')) {
            const idx = parseInt(href.replace('__cite_', '').replace('__', ''), 10);
            const src = sources && sources[idx];
            if (src && src.url) {
                const siteName = getSiteName(src.url);
                return (
                    <a
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={src.title || src.url}
                        className="inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 text-[11px] font-medium text-[#374151] hover:bg-[#F3F4F6] rounded-md border border-[#E5E7EB] no-underline transition-colors align-middle"
                        style={{ backgroundColor: '#F9FAFB', lineHeight: 1.4 }}
                    >
                        <SiteIcon url={src.url} siteName={siteName} size={14} />
                        <span className="whitespace-nowrap">{siteName}</span>
                    </a>
                );
            }
        }
        // Document citations in Hybrid mode: question.py's hybrid_instructions
        // now tells the model to write these as real markdown links shaped
        // "[Dateiname, Seite N](url)" — same shape Documents mode's own
        // SYSTEM_PROMPT already teaches, and the exact same generic
        // "<anything>, <non-numeric><number>" detection ContentRenderer.js's
        // `a` override uses to build a DocumentSourcePill there. Checked
        // AFTER the __cite_ branch above, so a real web citation (which also
        // has a comma+number-shaped site name only coincidentally, if ever)
        // is never misrouted here — __cite_ hrefs always match first.
        const rawText = Array.isArray(children) ? children.join('') : String(children ?? '');
        // The optional "(?:\s*[-–—]\s*\d+)?" tail handles a page RANGE
        // citation ("FYPFast.pdf, Seite 30–31") — without it, "\D*(\d+)" only
        // ever captures the first page number, then requires the string to
        // end right there, so the whole match failed for any range citation
        // and it fell through to the plain-<a> branch below with href="#"
        // (clicking it opened the same chat in a new tab instead of becoming
        // a pill). The inner group still captures just the FIRST page number
        // for navigation (a PDF viewer jumps to one page), while the outer
        // group keeps the full range text exactly as written for display.
        const docMatch = rawText.match(/^(.+),\s*\D*((\d+)(?:\s*[-–—]\s*\d+)?)\s*$/);
        if (docMatch) {
            const filename = docMatch[1].trim();
            const page = docMatch[2];
            const clickPage = docMatch[3];
            // Same disambiguation as ContentRenderer/ChatBlob's Sources panel —
            // this citation is plain text the model wrote (filename+page only,
            // no item_id), so look the match up in the structured doc sources
            // list to recover it before opening the file.
            let matchedDoc = (docSources || []).find(
                (s) => s?.filename?.trim().toLowerCase() === filename.toLowerCase() && String(s?.page) === String(clickPage)
            );
            if (!matchedDoc) {
                // The model can mangle an unusual filename when writing the
                // citation — if exactly one retrieved doc source has this
                // page number, there's no other file it could mean, so
                // resolve to it (and its real filename) instead of opening a
                // dead link for text the model half-invented.
                const samePage = (docSources || []).filter((s) => String(s?.page) === String(clickPage));
                if (samePage.length === 1) matchedDoc = samePage[0];
            }
            const displayFilename = matchedDoc?.filename || filename;
            return (
                <DocumentSourcePill
                    filename={displayFilename}
                    page={page}
                    href={href}
                    compact
                    folderPath={matchedDoc?.folderPath}
                    onClick={onSourceClick ? () => onSourceClick(displayFilename, clickPage, matchedDoc?.itemId) : undefined}
                />
            );
        }
        // The model sometimes writes a source's raw URL out again as an
        // ordinary link — e.g. literally answering "give me the link" — even
        // though that exact URL already has its own citation pill from the
        // [N] mechanism above (see citedUrls). Only suppressed when the link
        // TEXT is itself just the bare URL (nothing but "http://...") and
        // its href is a known-cited URL — a meaningfully-labeled link (e.g.
        // "Read the paper here") pointing to the same source still renders
        // normally, since hiding it would remove real information, not just
        // a redundant repeat of the URL.
        if (href && citedUrls.has(href.replace(/\/$/, '')) && /^https?:\/\//.test(rawText.trim())) {
            return null;
        }
        return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{children}</a>;
    };

    return (
        // md-ol-counter (see index.css): the model sometimes writes back-to-back
        // numbered lists that each restart at "1." — remark then parses them as
        // SEPARATE <ol> elements, each with its own native counter starting at 1
        // ("1., 1., 1." instead of "1., 2., 3."). A CSS counter shared across every
        // <ol> in this answer stays continuous regardless of how many separate
        // <ol> blocks got parsed, so `ol` below drops list-decimal (native
        // numbering) in favor of it.
        <div className="md-ol-counter">
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkNestLabeledListItems]}
                components={{
                    a: citationLink,
                    // font-normal here too, same inheritance reason as `li` below —
                    // a paragraph nested inside a bold label item shouldn't inherit it.
                    p: ({ children }) => <p className="mb-2 last:mb-0 font-normal">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="mb-2 space-y-1">{children}</ol>,
                    // Some answers write sub-section labels ("Pros"/"Cons", "Type", etc.)
                    // as their own bullet ("- **Pros**") instead of plain bold text —
                    // structurally that's a <li> whose ONLY content is a single <strong>
                    // (remarkNestLabeledListItems above already moved the bullets that
                    // conceptually belong to it into a real nested list here, as this
                    // li's own additional child — so a label-only li can have 1 child
                    // (just the strong) or 2 (strong + its now-nested sub-list)).
                    // Detected structurally, not by matching "Pros"/"Cons" text, so it
                    // works for whatever label the model chooses. Rendered without a
                    // bullet, bold, flush with the list's own left edge — reads as a
                    // sub-heading rather than another same-level point.
                    // The non-label branch needs its own explicit font-normal: these
                    // bullets are now physically nested INSIDE the bold label's <li>
                    // (that's the whole point of the plugin above), and font-weight is
                    // an inherited CSS property — without resetting it here, plain,
                    // never-bolded-by-the-model bullet text renders bold just because
                    // its new parent is.
                    li: ({ node, children }) => isLabelOnlyLiNode(node)
                        ? <li className="text-[14px] font-semibold list-none -ml-5 mt-2 first:mt-0">{children}</li>
                        : <li className="text-[14px] font-normal">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-bold mb-1">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                    // text-[14px], not Tailwind's `text-sm` — this app sets
                    // `html { font-size: 90% }` globally, so `text-sm`
                    // (0.875rem) resolves to ~12.6px here, visibly smaller
                    // than the 14px every other element in this renderer
                    // uses (the `li` overrides just above). That mismatch is
                    // the "source code font size looks strange" bug: code
                    // blocks rendering at a different size than the rest of
                    // the answer text around them.
                    code: ({ inline, children, className }) => {
                        if (inline ?? isInlineCode(className, children)) return <code className="bg-gray-100 px-1 rounded font-mono text-[14px]">{children}</code>;
                        const raw = Array.isArray(children) ? children.join('') : String(children ?? '');
                        // The model sometimes ALSO writes out a source's raw URL
                        // verbatim as its own line (e.g. answering "give me the
                        // link" literally), even though that exact same source
                        // already gets its own clickable pill from the [N]
                        // citation mechanism above — showing both is a genuine
                        // duplicate of the same link, not two different pieces
                        // of information. Any line that is ENTIRELY (nothing but,
                        // once trimmed) one of the message's own known source
                        // URLs is dropped here; every other line — including
                        // other URLs the model wrote that AREN'T a real cited
                        // source — still goes through linkifyCodeBlockUrls below
                        // exactly as before.
                        const knownUrls = new Set(
                            (sources || [])
                                .map((s) => s?.url?.replace(/\/$/, ''))
                                .filter(Boolean)
                        );
                        const dedupedRaw = raw
                            .split('\n')
                            .filter((line) => !knownUrls.has(line.trim().replace(/\/$/, '')))
                            .join('\n');
                        if (!dedupedRaw.trim()) return null;
                        return (
                            <pre className="bg-gray-100 rounded p-3 font-mono text-[14px] overflow-x-auto mb-2">
                                <code>{linkifyCodeBlockUrls(dedupedRaw)}</code>
                            </pre>
                        );
                    },
                    // Same styling/fix as ContentRenderer.js's table override —
                    // this renderer had none at all before, so a Hybrid-mode
                    // table fell back to bare unstyled markup AND could spill
                    // out past the chat bubble the same way Documents mode's
                    // table did before that fix.
                    table: ({ children }) => (
                        <div className="overflow-x-auto max-w-full">
                            <table className="border-collapse border border-gray-300 my-4 text-[14px]">{children}</table>
                        </div>
                    ),
                    thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
                    tbody: ({ children }) => <tbody>{children}</tbody>,
                    tr: ({ children }) => <tr className="border-b border-gray-300">{children}</tr>,
                    th: ({ children }) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-[14px]">{children}</th>,
                    td: ({ children }) => <td className="border border-gray-300 px-4 py-2 text-[14px]">{children}</td>,
                }}
            >
                {processed}
            </ReactMarkdown>
        </div>
    );
}

// One image attached to a user message. Two shapes cover it:
// - Just sent, this session: { filename, previewUrl } — previewUrl is a
//   local blob: URL from ChatInput's own staging, so this renders instantly
//   with no fetch at all.
// - Reloaded from the DB: { id, filename } — no previewUrl, since object
//   URLs don't survive a reload; fetches the real blob from /file/getfile
//   (same endpoint the Sidebar's own PDF/image viewer already uses) and
//   builds one locally instead.
function AttachedImageThumbnail({ image, userId, taskId, historyId, onClick, onLoad }) {
    const [fetchedUrl, setFetchedUrl] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (image.previewUrl || !image.filename) return;
        let cancelled = false;
        let objectUrl = null;
        fetch(`${BASE_FILEURL}/getfile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify({ user_id: userId, task_id: taskId, filename: image.filename, history_id: historyId }),
        })
            .then((res) => { if (!res.ok) throw new Error('fetch failed'); return res.blob(); })
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setFetchedUrl(objectUrl);
            })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image.filename]);

    const src = image.previewUrl || fetchedUrl;
    if (failed) return null;
    if (!src) {
        return <div className="w-20 h-20 rounded-lg bg-gray-100 animate-pulse flex-shrink-0" />;
    }
    return (
        <img
            src={src}
            alt={image.filename || ''}
            className="w-20 h-20 object-cover rounded-lg border border-gray-200 flex-shrink-0 cursor-pointer"
            onClick={onClick}
            // The parent's auto-scroll-to-bottom only watches
            // chatHistory/qaHistory (see Analysis.jsx) — a reload-fetched
            // thumbnail finishing its own async load changes the message's
            // rendered height independently of either, same class of bug
            // WebSearchLoadingIndicator's onProgressChange already fixed
            // for its own step-label changes. Without this, a tall image
            // can render partly below the fold with no re-scroll to reveal it.
            onLoad={onLoad}
        />
    );
}

const ChatBlob = ({ user, message, taskId, historyId, openSidebar, onRetry, onImageLoad, isAgentQuestion = false, sqId = null, agentQuestionTitle = '', readOnly = false, isLastAnswer = false, onActivateWordMode, onActivateDataAnalysisMode }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { aispaceColor } = useThemeColor();
    const name = localStorage.getItem("name");
    const userId = localStorage.getItem("id");
    const aiProfile = useSelector(selectAIProfile);
    const taskID = useSelector((state) => state.dropdown.id);
    const persistedTaskStatus = useSelector(state => sqId ? selectTaskStatusBySqId(state, sqId, historyId) : null);
    // Same file list ChatInput's own "/" picker reads — reused here only to
    // resolve a folder path for display, not fetched again.
    const userFilesForFolderLookup = useSelector((state) => state.api.userFiles);
    // Once this conversation has produced any document, the "make a Word
    // document" hint retires for good rather than reappearing under every
    // later answer.
    const generatedDocuments = useSelector((state) => state.api.generatedDocuments);
    const abr = getInitials(name);
    const [isShowContext] = useState(false);
    // Collapsed by default — shared by both the Web and Documents sources
    // sections below (only one of the two ever renders per message, so one
    // shared toggle is enough).
    const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);

   // for answer status
   const [statuses, setStatuses] = useState({});
   const [commentsOpen, setCommentsOpen] = useState(false);
   const [commentCount, setCommentCount] = useState(0);

   useEffect(() => {
     if (message && message.status) {
       setStatuses(prev => ({
         ...prev,
         [message.id || "default"]: message.status
       }));
     }
   }, [message, name]);

   const handleStatusChange = (answerId, statusKey) => {
     setStatuses(prev => ({
       ...prev,
       [answerId]: statusKey
     }));
   };


    // Simplified message content handling
    const messageContent = typeof message.content === 'string'
        ? message.content
        : message.content?.content || '';

    // ChatInput.jsx fills in a default prompt text ("Please analyze the
    // attached image.") when the user sends an image with no typed text at
    // all — the backend genuinely needs some prompt string to answer, but
    // showing that auto-generated text back to the user as if they'd typed
    // it looks wrong (they only ever picked an image). Re-checked here
    // (not skipped at send time) so it's hidden the same way whether this
    // is today's live message or one reloaded from the DB later.
    const isAutoImageOnlyMessage = Boolean(
        message.attachedImages?.length > 0 &&
        messageContent.trim() === t('chatActions.defaultImageOnlyMessage').trim()
    );

    // Get title if available
    const messageTitle = message.title || '';

    // Process sources using the enhanced validation system
    const rawSources = processMessageSources(message);
    // Folder path is resolved here, once, and carried on each source object —
    // every citation UI below (Sources panel, inline pills) reads it off the
    // source instead of re-resolving it itself.
    const sources = rawSources.map((s) => ({
      ...s,
      folderPath: resolveFolderPath(userFilesForFolderLookup?.files, s.filename, s.itemId, message.restrictAreaLabelsByFilename?.[s.filename]),
    }));

    const [loadingPdf, setLoadingPdf] = useState({});

    const isAnalysisPending = Boolean(message.analysisJob && message.analysisJob.status !== 'done');
    const handleAnalysisRetry = () => {
        const job = message.analysisJob;
        const retryJobId = job.job_id || job.retryOfJobId;
        dispatch(retryAnalysisJob({ localId: job.localId }));
        dispatch(startDataAnalysis({ ...job.payload, retry_job_id: retryJobId || null, local_id: job.localId }));
    };

    const handleSourceClick = async (filename, page, itemId) => {
        // Open the sidebar first
        openSidebar();
        
        // Set loading state for this specific source
        setLoadingPdf(prev => ({ ...prev, [`${filename}-${page}`]: true }));

        try {
            // Check which tab is currently active before opening the PDF
            const currentTab = localStorage.getItem('currentActiveTab');
            console.log('Current active tab before PDF open:', currentTab);
            
            // If we're in the Dateien tab, remember to return there
            if (currentTab === 'files') {
                localStorage.setItem('pdfOpenedFromTab', 'files');
                console.log('Will return to Dateien tab after closing PDF');
            } else {
                // Only set tasks tab if we're not explicitly in files tab
                localStorage.setItem('pdfOpenedFromTab', 'tasks');
            }
            
            // Get ID of the message/question that contains this source reference
            if (message) {
                // If we have a title that matches a question title (like "Spill"),
                // store that as our reference to find later
                let questionTitle = '';
                if (message.title) {
                    questionTitle = message.title;
                    console.log('Found question title:', questionTitle);
                }
                
                // Store both ID and title to ensure we can find the right question
                localStorage.setItem('pdfSourceMessageId', message.id || '');
                localStorage.setItem('pdfSourceMessageTitle', questionTitle);
                
                // Store the current timestamp for reference
                localStorage.setItem('pdfOpenTimestamp', Date.now().toString());
            }
            
            // Then dispatch the source actions with page number
            dispatch(addSource({ filename, page: parseInt(page), itemId }));
            
            if (userId && taskId) {
                await dispatch(fetchPDF({
                    userId,
                    taskId: taskId,
                    filename,
                    historyId: historyId,
                    page: parseInt(page),
                    itemId
                })).unwrap();
            }
        } catch (error) {
            console.error('Error loading PDF:', error);
        } finally {
            // Clear loading state
            setLoadingPdf(prev => ({ ...prev, [`${filename}-${page}`]: false }));
        }
    };

        // Function to render content with clickable source references
   

    console.log(message.context,'context')
    return (
        <div
            id={messageTitle ? `chat-message-${sanitizeTitleForId(messageTitle)}` : undefined}
            className={`flex w-full gap-3 ${user === "user" ? "justify-end" : "justify-start"}`}
        >
            {/* AI Avatar - Left side for assistant */}
            {user !== "user" && (
                <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-custom-yellow flex items-center justify-center">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M9.83705 9.63783L11.383 5H12.3317L13.8776 9.63783L18.5154 11.1838V12.1325L13.8776 13.6784L12.3317 18.3162H11.383L9.83705 13.6784L5.19922 12.1325V11.1838L9.83705 9.63783Z" fill="white" />
                        </svg>
                    </div>
                </div>
            )}

            {/* Message Content */}
            {/* min-w-0 on the AI branch: as a flex item, "flex-1" alone still
                defaults to min-width:auto, which refuses to shrink below its
                content's intrinsic width — a wide unbreakable child (e.g. an
                unwrapped code block, a long token) then pushes this whole
                column, and visually the answer box, wider than the chat area
                instead of wrapping/scrolling inside it. */}
            <div className={`flex flex-col ${user === "user" ? "items-end max-w-[70%]" : "flex-1 min-w-0"}`}>
                {/* User message bubble */}
                {user === "user" ? (
                    <>
                        {message.attachedImages?.length > 0 && (
                            <div className="flex flex-wrap gap-2 justify-end mb-1.5">
                                {message.attachedImages.map((image, idx) => (
                                    <AttachedImageThumbnail
                                        key={image.id || image.filename || idx}
                                        image={image}
                                        userId={userId}
                                        taskId={taskId}
                                        historyId={historyId}
                                        onLoad={onImageLoad}
                                    />
                                ))}
                            </div>
                        )}
                        {/* Which file(s) this question was restricted to — display-only, never part of `content` itself. */}
                        {message.restrictedFilenames?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 justify-end mb-1.5">
                                {message.restrictedFilenames.map((filename, idx) => (
                                    <span
                                        key={`${filename}-${idx}`}
                                        // Same shape as the composer chip, just muted (metadata, not part of the question).
                                        className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1 rounded-full bg-gray-50 border border-gray-100 text-[12.5px] text-gray-500 max-w-[220px]"
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                                            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                        <span className="truncate min-w-0" title={filename}>{filename}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                        {!isAutoImageOnlyMessage && (
                            <div className="bg-[#F3F4F6] px-4 py-3 rounded-2xl rounded-tr-md">
                                <ContentRenderer content={messageContent} />
                            </div>
                        )}
                        {/* Only for a timed-out request specifically (chatSlice keeps the
                            question on screen and attaches retryPayload only when
                            errorType === 'timeout') — every other failure keeps the
                            pre-existing behavior of removing the question instead, since
                            a blind retry isn't the right default for those. */}
                        {message.failed && message.errorType === 'timeout' && (
                            <button
                                type="button"
                                onClick={() => onRetry?.(message.retryPayload)}
                                className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1 text-[12px] font-medium text-[#DC2626] hover:bg-[#FEF2F2] rounded-md transition-colors"
                            >
                                <RotateCcw size={13} />
                                {t('chatActions.retryTimeoutLabel')}
                            </button>
                        )}
                    </>
                ) : (
                    /* AI Response */
                    <div className="w-full">
                        {/* Agent question title pill — only for sidebar-predefined
                            questions. Sticky: this is the actual "Standard Question
                            tag" from Timon's Teams mockup — it pins to the top while
                            its own answer scrolls underneath, then hands off to the
                            next tag. Its containing block is this message's own
                            `div.w-full` answer wrapper (a sibling to nothing else),
                            so it naturally releases exactly when this answer ends —
                            no cross-message grouping needed for this one. */}
                        {isAgentQuestion && agentQuestionTitle && (
                            <div
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium text-white mb-3 w-full sticky top-0 z-10"
                                style={{ backgroundColor: aispaceColor }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M9.83705 9.63783L11.383 5H12.3317L13.8776 9.63783L18.5154 11.1838V12.1325L13.8776 13.6784L12.3317 18.3162H11.383L9.83705 13.6784L5.19922 12.1325V11.1838L9.83705 9.63783Z" fill="currentColor" />
                                </svg>
                                <span className="truncate">{t('chatActions.agentQuestionPrefix', { title: agentQuestionTitle })}</span>
                            </div>
                        )}
                        {/* Word-generation result — the WHOLE lifecycle
                            ("Creating…" -> finished card) lives inside this
                            one message, no new message is ever added (spec).
                            message.wordJob is present (job_id null or set)
                            from the moment the user hits send — see
                            addPendingWordJob in chatSlice.js — so this card
                            shows immediately instead of the generic
                            "AI is responding" dots; WordJobStatus itself
                            doesn't start polling until jobId is non-null
                            (its poll effect no-ops on a null jobId). Or,
                            already finished before this page loaded
                            (message.generated_document_id, straight from the
                            reload data — no polling needed). */}
                        {isAnalysisPending ? (
                            <AnalysisJobCard
                                job={message.analysisJob}
                                historyId={historyId}
                                userId={userId}
                                t={t}
                                onRetry={readOnly ? undefined : handleAnalysisRetry}
                            />
                        ) : message.wordJob ? (
                            <WordJobStatus
                                jobId={message.wordJob.job_id}
                                userId={userId}
                                historyId={historyId}
                                t={t}
                                aispaceColor={aispaceColor}
                                openSidebar={openSidebar}
                            />
                        ) : message.generated_document_id ? (
                            <WordResultCard
                                title={message.generated_document_title}
                                filename={message.generated_document_filename}
                                fileSize={message.generated_document_file_size}
                                documentId={message.generated_document_id}
                                userId={userId}
                                t={t}
                                aispaceColor={aispaceColor}
                                openSidebar={openSidebar}
                            />
                        ) : (
                        <>
                        {/* Content */}
                        <div className="text-[14px] text-[#374151] leading-relaxed">
                            {isShowContext ? (
                                message?.context?.map((ctx, index) => (
                                    <div key={index} className="mb-4">
                                        <h1 className='font-bold text-lg'>{t('analysis.documentNumber', { index: index + 1 })}</h1>
                                        <ContentRenderer content={ctx.text} />
                                        <div className='text-sm text-gray-500'>
                                            {t('analysis.sourceLabel', { filename: ctx.Dateiname, page: ctx.Seitenzahl })}
                                        </div>
                                    </div>
                                ))
                            ) : message.web_search ? (
                                // message.source (singular) is always the web-shaped {title,url}
                                // array; message.sources (plural) is doc-shaped {filename,page}
                                // and, for hybrid messages, is populated alongside it — so source
                                // must be checked first or hybrid would wrongly render doc citations
                                // where the [1]/[2] web links belong.
                                <WebSearchContent content={messageContent} sources={message.source || message.sources} docSources={sources} onSourceClick={handleSourceClick} />
                            ) : (
                                // onSourceClick: inline "[Dateiname, Seite N]" citations open the
                                // PDF in this app's own sidebar (same handleSourceClick the
                                // Sources panel below already uses), not a new browser tab.
                                <ContentRenderer content={messageContent} sources={sources} onSourceClick={handleSourceClick} />
                            )}
                        </div>

                        {/* AI-generated disclaimer, with the "make a Word document"
                            hint sharing the same row (right-aligned) instead of its
                            own line — costs no extra height. The hint is clickable,
                            only under the most recent answer (not every past
                            message), and retires for good once this conversation has
                            produced any document. Never keyword-triggered (spec:
                            "Do not automate this") — visibility depends only on
                            aiProfile/isLastAnswer/generatedDocuments state, never on
                            answer content. Clicking already arms the Word toggle and
                            focuses the input, so the label just names the action
                            rather than explaining it. */}
                        {message.analysis && <AnalysisResultPanel analysis={message.analysis} t={t} />}

                        <div className="mt-2 flex items-center justify-between gap-3">
                            <p className="text-[12px] italic text-[#9CA3AF]">
                                {t('chatActions.aiDisclaimer')}
                            </p>
                            {!readOnly && isLastAnswer && (message.analysis || message.analysisJob) && Boolean(aiProfile?.code_interpreter_enabled) && (
                                <button
                                    type="button"
                                    onClick={() => onActivateDataAnalysisMode?.()}
                                    className="flex-shrink-0 inline-flex items-center gap-1 text-[12px] text-[#9CA3AF] hover:underline hover:text-[#6B7280]"
                                >
                                    <SquareTerminal size={14} />
                                    {t('dataAnalysis.hint')}
                                </button>
                            )}
                            {!readOnly && !message.analysis && !message.analysisJob && (!aiProfile || Boolean(aiProfile.word_enabled)) && !message.generated_document_id && isLastAnswer && generatedDocuments.length === 0 && (
                                <button
                                    type="button"
                                    onClick={() => onActivateWordMode?.()}
                                    className="flex-shrink-0 inline-flex items-center gap-1 text-[12px] text-[#9CA3AF] hover:underline hover:text-[#6B7280]"
                                >
                                    <WordIcon size={14} />
                                    {t('chatActions.wordHint')}
                                </button>
                            )}
                        </div>

                        {/* Sources section — ONE collapsible panel shared by Web, Documents,
                            and Hybrid answers alike (same container/header, collapsed by
                            default, same compact pill grid). Hybrid messages can have BOTH
                            web sources (message.source/sources, {title,url}) and document
                            sources (`sources` below, {filename,page} via
                            processMessageSources) at once — both render together in the
                            same grid rather than two separate panels, so a mixed answer
                            still shows just one "Sources" section. Header text/icon only
                            changes when a message is genuinely mixed; a message with only
                            one type keeps the exact label it always had (Web mode's own
                            "Web Sources (N)" with a globe icon, or Documents mode's plain
                            "Sources"), so neither mode's existing look changes. */}
                        {(() => {
                            const webSources = (message.source || message.sources || []).filter(s => s?.url);
                            const docSources = sources;
                            if (isShowContext || (webSources.length === 0 && docSources.length === 0)) return null;
                            const isMixed = webSources.length > 0 && docSources.length > 0;
                            return (
                                <div className="mt-4 border border-[#E5E7EB] rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => setIsSourcesExpanded(!isSourcesExpanded)}
                                        className="w-full flex items-center justify-between px-4 py-3 bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors"
                                    >
                                        <span className="flex items-center gap-2 text-[14px] font-medium text-[#374151]">
                                            {docSources.length === 0 && (
                                                <Globe size={14} className="text-[#9CA3AF]" />
                                            )}
                                            {docSources.length === 0
                                                ? t('analysis.webSourcesCount', { count: webSources.length })
                                                : isMixed
                                                    ? t('analysis.sourcesCount', { count: webSources.length + docSources.length })
                                                    : t('analysis.sources')}
                                        </span>
                                        {isSourcesExpanded ? (
                                            <ChevronUp size={18} className="text-[#6B7280]" />
                                        ) : (
                                            <ChevronDown size={18} className="text-[#6B7280]" />
                                        )}
                                    </button>

                                    {isSourcesExpanded && (
                                        <div className="px-4 py-3 grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2">
                                            {webSources.map((source, index) => (
                                                <a
                                                    key={`web-${index}`}
                                                    href={source.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex w-full items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white no-underline shadow-sm hover:shadow-md hover:border-[#D1D5DB] hover:-translate-y-px transition-all"
                                                >
                                                    <SiteIcon url={source.url} siteName={getSiteName(source.url)} size={16} />
                                                    <span className="flex-1 min-w-0 text-[13px] text-[#111827] font-medium truncate">
                                                        {getSiteName(source.url)}
                                                    </span>
                                                    <span className="text-[12.5px] text-[#9CA3AF] whitespace-nowrap flex-shrink-0 pl-2 border-l border-[#E5E7EB]">
                                                        {getDomain(source.url)}
                                                    </span>
                                                </a>
                                            ))}
                                            {/* Same DocumentSourcePill used for the inline
                                                "[Dateiname, Seite N]" citations ContentRenderer/
                                                WebSearchContent render inside the answer text
                                                itself, so both look identical. */}
                                            {docSources.map((source, index) => {
                                                if (!isSourceClickable(source)) {
                                                    console.warn(`Source at index ${index} is not clickable:`, source);
                                                    return null;
                                                }
                                                const isLoading = loadingPdf[`${source.filename}-${source.page}`];

                                                return (
                                                    <div key={`doc-${index}`} className="relative flex">
                                                        <DocumentSourcePill
                                                            filename={source.filename}
                                                            page={source.page}
                                                            folderPath={source.folderPath}
                                                            fullWidth
                                                            onClick={() => !isLoading && handleSourceClick(source.filename, source.page, source.itemId)}
                                                        />
                                                        {isLoading && (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-full">
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#7C3AED]"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        </>
                        )}

                        {/* Comment Section — hidden in read-only shared view,
                            and for a Word-generation result (live or
                            reloaded): speaker/copy/comment/Quick actions
                            don't apply to a document deliverable the way
                            they do to a normal AI answer. */}
                        {!readOnly && !message.wordJob && !message.generated_document_id && !isAnalysisPending && <CommentSection
                            user={user === "user" ? { name: name } : { name: name }}
                            answer={messageContent}
                            taskId={taskId}
                            historyId={historyId}
                            onStatusChange={(statusKey) => handleStatusChange(message.id || "default", statusKey)}
                            existingStatus={persistedTaskStatus?.answer_status || statuses[message.id || "default"] || null}
                            showStatus={isAgentQuestion}
                            sqId={sqId}
                            commentCount={commentCount}
                            commentsOpen={commentsOpen}
                            onToggleComments={() => setCommentsOpen((v) => !v)}
                        />}

                        {user !== "user" && message.id && !message.wordJob && !message.generated_document_id && !isAnalysisPending && (
                            <AnswerComments
                                questionId={message.id}
                                historyId={historyId}
                                open={readOnly ? undefined : commentsOpen}
                                onCountChange={setCommentCount}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* User Avatar - Right side for user */}
            {user === "user" && (
                <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: aispaceColor }}>
                        <span className="text-white text-[12px] font-semibold">{abr}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBlob;